import React from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { Box, CircularProgress, Button } from "@mui/material";

import * as Yup from "yup";

import {
  useGetDataSources,
  useGetDataSourcesConnections,
  useUpdateDataSourcesUpload,
  useDeleteDataSources,
  useUpdateDataSourcesStatus,
  useGetDataSourcesDetails,
} from "../hooks";

import AddIcon from "@mui/icons-material/Add";
import DeleteFile from "./modals/DeleteFile";
import PlatformSection from "./PlatformSection";
import SnackbarComponent from "./shared/SnackBarComponent";
import SnackbarSuccessComponent from "./shared/SnackBarSuccessComponent";

interface PlatformsComponentProps {
  clientId: any;
  scrollToSectionId: any;
}

const generateUniqueId = () =>
  `platform_${new Date().getTime()}_${Math.random().toString(36).substr(2, 9)}`;

const PlatformsComponent: React.FC<PlatformsComponentProps> = ({
  clientId,
  scrollToSectionId,
}) => {

  console.log("scrollToSectionId", scrollToSectionId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: getDataSourcesConnections,
    isLoading: isGetDataSourcesConnectionsPending,
    error: getDataSourcesConnectionsError,
  } = useGetDataSourcesConnections(clientId);

  const {
    data: getDataSources,
    isLoading: isGetDataSourcesPending,
    error: getDataSourcesError,
  } = useGetDataSources();

  const { mutate: uploadDataSources, isPending: isUploadDataSourcesPending } =
    useUpdateDataSourcesUpload();

  const { mutate: deleteDataSources, isPending: isDeleteDataSourcesPending } =
    useDeleteDataSources();

  const {
    mutate: updateDataSourcesStatus,
    isPending: isUpdateDataSourcesStatusPending,
  } = useUpdateDataSourcesStatus();

  



  const [error, setError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  const [success, setSuccess] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState("");

  // Available platforms for dropdown
  const [availablePlatforms, setAvailablePlatforms] = React.useState([]);
  const [isSaving, setIsSaving] = React.useState(false);

  const [platformSections, setPlatformSections] = React.useState<any>([]);
  const [tempPlatformSections, setTempPlatformSections] = React.useState<any>([]);

  // State for dialog management
  const [deleteDialog, setDeleteDialog] = React.useState(false);
  const [selectedPlatform, setSelectedPlatform] = React.useState<any>(null);

  const [actionType, setActionType] = React.useState("");

  const transformInitialData = () => {
    if (!getDataSourcesConnections || !getDataSourcesConnections.data) {
      return [];
    }
    if (getDataSourcesConnections?.data?.length === 0) {
      return [
        {
          id: generateUniqueId(),
          clientHasDataSourceid: null,
          platform: "",
          connection: "",
          accountId: "",
          description: "",
          status: false,
          errors: {},
          touched: {},
          isEditing: true, // Set to true for a new section
          isModified: false,
          originalData: {},
          successMessage: "",
          errorMessage: "",
          isNew: true, // Mark as new
        },
      ];
    }
    return getDataSourcesConnections?.data?.map((item: any) => ({
      id: generateUniqueId(),
      clientHasDataSourceid: item.clientHasDataSourceId,
      platform: item.dataSourceId.toString(),
      connection: item.dataSourceConnectionId.toString(),
      accountId: item.accountId,
      description: item.description,
      status: item.status,
      errors: {},
      touched: {},
      isEditing: false,
      isModified: false,
      originalData: { ...item },
      successMessage: "",
      errorMessage: "",
      isNew: false,
    }));
  };

  const sectionValidationSchema = Yup.object({
    platform: Yup.string().required("Data Source is required"),
    connection: Yup.string().required("Connection is required"),
    accountId: Yup.string().required("Account ID is required"),
    description: Yup.string(),
  }).test(
    "unique-combo",
    "This combination of Data Source, Connection, and Account ID already exists",
    function (value) {
      // This will only be checked during final validation, not during field-level validation
      const { platform, connection, accountId } = value;
      return true; // We'll do the actual check in handleFieldChange and handleSubmit
    }
  );

  React.useEffect(() => {
    if (getDataSources) {
      setAvailablePlatforms(getDataSources);
    }
  }, [getDataSources]);

  console.log("platformSections", platformSections)
  console.log("scrollToSectionId", scrollToSectionId)

  React.useEffect(() => {
    if (getDataSourcesConnections?.data) {
      setPlatformSections(transformInitialData());
      setTempPlatformSections(transformInitialData())
    }
  }, [getDataSourcesConnections?.data, !isGetDataSourcesConnectionsPending]);


  const sectionRefs : any = React.useRef([]);

  React.useEffect(() => {
    if (scrollToSectionId && tempPlatformSections.length > 0) {
      const sectionIndex = tempPlatformSections.findIndex(
        (section : any) => section.clientHasDataSourceid === scrollToSectionId
      );

      console.log("sectionIndex", sectionIndex)
      if (sectionIndex !== -1 && sectionRefs.current[sectionIndex]) {
        sectionRefs.current[sectionIndex].scrollIntoView({
          behavior: "smooth",
          block: "start",
        });

        sectionRefs.current[sectionIndex].classList.add("selectedDataSource");

        setTimeout(() => {
          if (sectionRefs.current[sectionIndex]) {
            sectionRefs.current[sectionIndex].classList.remove("selectedDataSource");
          }
        }, 3000);
      }
    }
  }, [scrollToSectionId, tempPlatformSections]);

  const toggleEditMode = (sectionId: any) => {
    setPlatformSections((prevSections: any) => {
      return prevSections.map((section: any) => {
        if (section.id === sectionId) {
          if (section.isEditing) {
            // If we're going from edit to view, mark as modified
            return {
              ...section,
              isEditing: false,
              isModified: true,
              successMessage: "",
              errorMessage: "",
            };
          } else {
            // Going from view to edit
            return {
              ...section,
              isEditing: true,
              originalData: { ...section },
              successMessage: "",
              errorMessage: "",
            };
          }
        }
        return section;
      });
    });
  };

  const cancelEdit = (sectionId: any) => {
    setPlatformSections((prevSections: any) => {
      // Check if the section is new before filtering
      const sectionToCancel = prevSections.find((section: any) => section.id === sectionId);
      
      if (sectionToCancel && sectionToCancel.isNew) {
        // If it's a new section, remove it from the array completely
        return prevSections.filter((section: any) => section.id !== sectionId);
      } else {
        // For existing sections, restore from originalData as before
        return prevSections.map((section: any) => {
          if (section.id === sectionId) {
            return {
              ...section.originalData,
              isEditing: false,
              isModified: false,
              isNew: false,
              originalData: { ...section.originalData },
              successMessage: "",
              errorMessage: "",
            };
          }
          return section;
        });
      }
    });
  };

  const cancelEdit1 = (sectionId: any) => {
    setPlatformSections((prevSections: any) => {
      return prevSections.map((section: any) => {
        if (section.id === sectionId) {
          // Check if this is a new section (you could use any logic that identifies a new section)
          const isNewSection = section.isNew; // Assuming you have an 'isNew' flag
          console.log("isNewSection", isNewSection);
          if (isNewSection) {
            // For new sections, keep isEditing as true
            return {
              id: sectionId,
              clientHasDataSourceid: null,
              platform: "",
              connection: "",
              accountId: "",
              description: "",
              status: false,
              errors: {},
              touched: {},
              isEditing: false, // Keep it editable
              isModified: false,
              isNew: true,
              originalData: {
                id: sectionId,
                clientHasDataSourceid: null,
                platform: "",
                connection: "",
                accountId: "",
                description: "",
                status: false,
                errors: {},
                touched: {},
                originalData: {},
                isEditing: true,
                isModified: false,
                isNew: true,
              },
              successMessage: "",
              errorMessage: "",
            };
          } else {
            // For existing sections, restore from originalData and set isEditing to false
            return {
              ...section.originalData,
              isEditing: false,
              isModified: false,
              isNew: false,
              originalData: { ...section.originalData },
              successMessage: "",
              errorMessage: "",
            };
          }
        }
        return section;
      });
    });
  };

  const checkForDuplicates = (values: any) => {
    // Only check if we have values for all three fields
    if (values.platform && values.connection && values.accountId) {
      // Find any other section with the same platform, connection, and accountId combination
      const isUnchanged = platformSections.some(
        (section: any) =>
          section.id === values.id && // Find the section with matching ID
          section.originalData.platform === values.platform &&
          section.originalData.connection === values.connection &&
          section.originalData.accountId === values.accountId &&
          section.originalData.description === values.description &&
          section.originalData.status === values.status
      );

      return isUnchanged;
    }
  };

  const handleFieldBlur = async (sectionId: any, field: any, event?: any) => {
    console.log(
      "handleFieldBlur called for field:",
      field,
      "in section:",
      sectionId
    );

    // Find the section
    const sectionIndex = platformSections.findIndex(
      (section: any) => section.id === sectionId
    );
    if (sectionIndex === -1) return;

    const section = platformSections[sectionIndex];

    // Mark the field as touched regardless of validation outcome
    setPlatformSections((prevSections: any) => {
      return prevSections.map((s: any) => {
        if (s.id === sectionId) {
          return {
            ...s,
            touched: {
              ...s.touched,
              [field]: true,
            },
            isModified: true, // Also mark as modified when field is touched
          };
        }
        return s;
      });
    });

    try {
      // Validate just the current field
      // Handle potential null/undefined values
      const fieldValue = section[field] || "";
      await Yup.reach(sectionValidationSchema, field).validate(fieldValue);

      // Clear error for this field if validation passes
      setPlatformSections((prevSections: any) => {
        return prevSections.map((s: any) => {
          if (s.id === sectionId) {
            const updatedErrors = { ...s.errors };
            delete updatedErrors[field];

            return {
              ...s,
              errors: updatedErrors,
            };
          }
          return s;
        });
      });
    } catch (err: any) {
      // Set error for this field
      setPlatformSections((prevSections: any) => {
        return prevSections.map((s: any) => {
          if (s.id === sectionId) {
            return {
              ...s,
              errors: {
                ...s.errors,
                [field]: err.message,
              },
            };
          }
          return s;
        });
      });
    }
  };

  const handleFieldChange = async (sectionId: any, field: any, value: any) => {
    console.log("handleFieldChange for", field, "with value:", value);

    // Find the section
    const sectionIndex = platformSections.findIndex(
      (section: any) => section.id === sectionId
    );
    if (sectionIndex === -1) return;

    const currentSection = platformSections[sectionIndex];

    // Check for duplicates when any of the unique fields are changed
    let isDuplicate = false;
    if (["platform", "connection", "accountId"].includes(field)) {
      // Create "would-be" values after this change
      const platformToCheck =
        field === "platform" ? value : currentSection.platform;
      const connectionToCheck =
        field === "connection" ? value : currentSection.connection;
      const accountIdToCheck =
        field === "accountId" ? value : currentSection.accountId;

      // Check for duplicate combos in other sections
      isDuplicate = platformSections.some((section: any) => {
        // Skip comparing with itself
        if (section.id === sectionId) return false;

        // Convert to strings for comparison to handle mixed types
        const platformMatch =
          String(section.platform) === String(platformToCheck);
        const connectionMatch =
          String(section.connection) === String(connectionToCheck);
        const accountIdMatch =
          String(section.accountId) === String(accountIdToCheck);

        // Return true if this combo already exists in another section
        return platformMatch && connectionMatch && accountIdMatch;
      });
    }

    // Begin updates to the section
    setPlatformSections((prevSections: any) => {
      return prevSections.map((section: any) => {
        if (section.id === sectionId) {
          // Always mark field as touched
          const updatedTouched = {
            ...section.touched,
            [field]: true,
          };

          // Handle duplicate case
          if (isDuplicate) {
            return {
              ...section,
              [field]: value,
              touched: updatedTouched,
              isModified: true,
              errors: {
                ...section.errors,
                uniqueCombo:
                  "This combination of Data Source, Connection and Account ID already exists",
                duplicatePlatform: field === "platform" ? true : false,
                duplicateConnection: field === "connection" ? true : false,
                duplicateAccountId: field === "accountId" ? true : false,
              },
            };
          }

          // Create a copy of current errors to modify
          const updatedErrors = { ...section.errors };

          // Clear duplicate-related errors
          delete updatedErrors.uniqueCombo;
          delete updatedErrors.duplicatePlatform;
          delete updatedErrors.duplicateConnection;
          delete updatedErrors.duplicateAccountId;

          // Validate the field value
          // Handle empty values - mark as error if field is required
          if (value === "" || value === null || value === undefined) {
            // Set error for required field
            if (field === "accountId") {
              updatedErrors.accountId = "Account ID is required";
            } else if (field === "platform") {
              updatedErrors.platform = "Data Source is required";
            } else if (field === "connection") {
              updatedErrors.connection = "Connection is required";
            }
          } else {
            // Clear error for this field when a value is provided
            delete updatedErrors[field];
            delete updatedErrors[`${field}Required`];

            // For accountId specifically, clear any validation errors
            if (field === "accountId") {
              delete updatedErrors.accountId;
              delete updatedErrors.accountIdRequired;
              delete updatedErrors.accountIdInvalid;
            }
          }

          // Special case for platform field - reset connection when platform changes
          if (field === "platform" && section.platform !== value) {
            return {
              ...section,
              [field]: value,
              connection: "", // Reset connection when platform changes
              touched: updatedTouched,
              isModified: true,
              successMessage: "",
              errorMessage: "",
              errors: updatedErrors,
            };
          }

          // Regular field change
          return {
            ...section,
            [field]: value,
            touched: updatedTouched,
            isModified: true,
            successMessage: "",
            errorMessage: "",
            errors: updatedErrors,
          };
        }
        return section;
      });
    });

    // If value is not empty, we can perform additional async validation here
    // This replaces the async validation from handleFieldBlur
    if (value !== "" && value !== null && value !== undefined) {
      try {
        // Run Yup validation for the field
        await Yup.reach(sectionValidationSchema, field).validate(value);

        // If validation passes, clear any errors for this field
        setPlatformSections((prevSections: any) => {
          return prevSections.map((s: any) => {
            if (s.id === sectionId) {
              const updatedErrors = { ...s.errors };
              delete updatedErrors[field];

              return {
                ...s,
                errors: updatedErrors,
              };
            }
            return s;
          });
        });
      } catch (err: any) {
        // If validation fails, set the error message
        setPlatformSections((prevSections: any) => {
          return prevSections.map((s: any) => {
            if (s.id === sectionId) {
              return {
                ...s,
                errors: {
                  ...s.errors,
                  [field]: err.message,
                },
              };
            }
            return s;
          });
        });
      }
    }
  };

  const toggleSectionActive = (item: any) => {
    if (!item.isNew) {
      setActionType("PlatformStatus");
      setDeleteDialog(true);
      setSelectedPlatform(item);
    } else {
      setPlatformSections((prevSections: any) => {
        return prevSections.map((section: any) => {
          if (section.id === item.id) {
            return { ...section, status: !item.status };
          }
          return section;
        });
      });
    }
  };

  const onUploadDataSourcesSuccess = (res: any, sectionId: any) => {
    console.log("res", res);
    setIsSaving(false);

    queryClient.invalidateQueries({ queryKey: ["platforms"] });

    // Update the specific section with success message
    setTimeout(() => {
      setPlatformSections((prevSections: any) =>
        prevSections.map((section: any) =>
          section.id === sectionId
            ? {
                ...section,
                successMessage: "Success! Data Source saved successfully.",
                errorMessage: "",
                isEditing: false,
                isModified: false,
                isNew: false,
              }
            : section
        )
      );
    }, 100);
  };

  const onUploadDataSourcesError = (err: any, sectionId: any) => {
    console.log("err", err);

    // Update the specific section with error message
    setPlatformSections((prevSections: any) =>
      prevSections.map((section: any) =>
        section.id === sectionId
          ? {
              ...section,
              errorMessage: err?.data?.message || "Error! Failed to save Data Source.",
              successMessage: "",
            }
          : section
      )
    );

    // Re-enable save buttons
    setIsSaving(false);
  };

  const handleSubmit = async (sectionId: any) => {
    console.log("sectionId", sectionId);

    setIsSaving(true);
    // Find the section
    const sectionIndex = platformSections.findIndex(
      (section: any) => section.id === sectionId
    );

    if (sectionIndex === -1) {
      setIsSaving(false);
      return;
    }

    const sectionToSave: any = platformSections[sectionIndex];

    console.log("sectionToSave", sectionToSave);

    try {
      // Validate this section
      await sectionValidationSchema.validate(sectionToSave, {
        abortEarly: false,
      });

      // If we got here, validation passed
      // Transform the section data to the API format
      const sectionData = {
        clientHasDataSourceid: sectionToSave.clientHasDataSourceid || 0,
        dataSourceid: parseInt(sectionToSave.platform),
        dataSourceConnectionid: parseInt(sectionToSave.connection),
        accountid: sectionToSave.accountId,
        description: sectionToSave.description,
        status: sectionToSave.status,
      };

      console.log("Saving section data:", sectionData);

      const reqBody = {
        id: clientId,
        body: [sectionData],
      };

      console.log("reqBody", reqBody);

      uploadDataSources(reqBody, {
        onSuccess: (res) => onUploadDataSourcesSuccess(res, sectionId),
        onError: (err) => onUploadDataSourcesError(err, sectionId),
      });

      // Make API call to save this section
      // await api.updatePlatformSection(sectionData);

      // Update section state to show it's no longer modified or being edited
      // setPlatformSections((prevSections: any) => {
      // return prevSections.map((section: any, idx: any) => {
      //   if (idx === sectionIndex) {
      //  return {
      // ...section,
      // isEditing: false,
      // isModified: false,
      // errors: {},
      //  originalData: { ...section }, // Update original data
      //  };
      //  }
      //  return section;
      //  });
      //  });
    } catch (err: any) {
      console.error("Section validation error:", err);

      // Handle validation errors
      const errors: any = {};
      if (err.inner && err.inner.length > 0) {
        err.inner.forEach((e: any) => {
          errors[e.path] = e.message;
        });
      } else {
        // Handle other types of errors
        errors.general = err.message || "An error occurred";
      }

      setIsSaving(false);

      // Update section with validation errors
      setPlatformSections((prevSections: any) => {
        const updatedSections: any = [...prevSections];
        updatedSections[sectionIndex] = {
          ...updatedSections[sectionIndex],
          errors,
          touched: {
            platform: true,
            connection: true,
            accountId: true,
            description: true,
          },
        };
        return updatedSections;
      });
    }
  };

  const handleAddPlatform = () => {
    setPlatformSections([
      ...(platformSections || []),
      {
        id: generateUniqueId(),
        status: false,
        platform: "",
        connection: "",
        accountId: "",
        description: "",
        errors: {},
        touched: {},
        isEditing: true,
        isModified: false,
        originalData: {},
        successMessage: "",
        errorMessage: "",
        isNew: true,
      },
    ]);
  };

  const handleDelete = (item: any) => {
    console.log("section", item);
    if (!item.isNew) {
      setActionType("Delete");
      setDeleteDialog(true);
      setSelectedPlatform(item);
    } else {
      setPlatformSections(
        platformSections.filter((section: any) => section.id !== item.id)
      );
      setSelectedPlatform(null);
    }
  };

  const confirmDelete = () => {
    console.log("selectedPlatform", selectedPlatform, actionType);

    if (actionType == "Delete") {
      if (platformSections.length > 0) {
        if (!selectedPlatform.isNew) {
          deleteDataSources(selectedPlatform.clientHasDataSourceid, {
            onSuccess: (res) => onDeleteDataSourcesSuccess(res, selectedPlatform),
            onError: onDeleteDataSourcesError,
          });
          //setPlatformSections(
          // platformSections.filter(
          //  (section: any) => section.id !== selectedPlatform
          //  )
          // );
          //setDeleteDialog(false);
          //setSelectedPlatform(null);
        } else {
          setPlatformSections(
            platformSections.filter(
              (section: any) => section.id !== selectedPlatform
            )
          );
          setDeleteDialog(false);
          setSelectedPlatform(null);
        }
      } else {
        // If it's the last section, just reset it instead of removing
        setPlatformSections([]);
        setDeleteDialog(false);
        setSelectedPlatform(null);
      }
    } else if (actionType == "Status" || actionType == "PlatformStatus") {
      setPlatformSections((prevSections: any) => {
        return prevSections.map((section: any) => {
          if (section.id === selectedPlatform.id) {
            return { ...section, status: !selectedPlatform.status };
          }
          return section;
        });
      });
      setDeleteDialog(false);
      setSelectedPlatform(null);
      setActionType("");
     // const body = {
     //   id: selectedPlatform.clientHasDataSourceid,
     //   reqBody: { status: !selectedPlatform.active },
     // };
      //updateDataSourcesStatus(body, {
      //  onSuccess: onUpdateDataSourcesStatusSuccess,
      //  onError: onUpdateDataSourcesStatusError,
      //});
    }
  };

  const onDeleteDataSourcesSuccess = (res: any, item: any) => {
    const deletedSuccessMessage = `This Data Source has been successfully deleted.`;
    setSuccess(true);
    setSuccessMessage(deletedSuccessMessage);
    setDeleteDialog(false);
    setSelectedPlatform(null);
    setActionType("");
    queryClient.invalidateQueries({ queryKey: ["platforms"] });
    setPlatformSections(
      platformSections.filter((section: any) => section.id !== item.id)
    );
  };

  const onDeleteDataSourcesError = (err: any) => {
    setDeleteDialog(false);
    setSelectedPlatform(null);
    setActionType("");
    setError(true);
    setErrorMessage(err?.data?.message);
  };

  const onUpdateDataSourcesStatusSuccess = (res: any) => {
    const successMessage = `This Data Source has been changed to ${!selectedPlatform.status}`;
    setSuccess(true);
    setSuccessMessage(successMessage);
    setDeleteDialog(false);
    setSelectedPlatform(null);
    setActionType("");
    queryClient.invalidateQueries({ queryKey: ["platforms"] });
  };

  const onUpdateDataSourcesStatusError = (err: any) => {
    setDeleteDialog(false);
    setSelectedPlatform(null);
    setActionType("");
    setError(true);
    setErrorMessage(err?.data?.message);
  };

  const handleDeleteFileCancel = () => {
    setDeleteDialog(false);
    setSelectedPlatform(null);
    setActionType("");
  };

  if (isGetDataSourcesConnectionsPending) {
    return (
      <Box className="hvCenter">
        <CircularProgress size="1.5rem" sx={{ mt: 1.5, mb: 1 }} />
      </Box>
    );
  }

  function disableAddButton() {
    return platformSections?.some((section: any) => section?.isNew === true);
  }

  function disableEditButton() {
    return platformSections?.some(
      (section: any) => section?.isEditing === true
    );
  }

  const hideError = () => {
    setError(false);
    setErrorMessage("");
  };

  const hideSuccess = () => {
    setSuccess(false);
    setSuccessMessage("");
  };


  return (
    <Box>
      {platformSections?.map((section: any, index: any) => {
        return (
          <PlatformSection
            key={section.id}
            ref={(el : any) => (sectionRefs.current[index] = el)}
            section={section}
            onDelete={handleDelete}
            onFieldChange={handleFieldChange}
            onFieldBlur={handleFieldBlur}
            onToggleActive={toggleSectionActive}
            availablePlatforms={availablePlatforms}
            toggleEditMode={toggleEditMode}
            cancelEdit={cancelEdit}
            handleSubmit={handleSubmit}
            isSaving={isSaving}
            checkForDuplicates={checkForDuplicates}
            isEditDisable={disableEditButton()}
            isNewSection={disableAddButton()}
            highlight={section.clientHasDataSourceid === scrollToSectionId}
          />
        );
      })}

      {!disableEditButton() && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          className="btn-outlined"
          onClick={handleAddPlatform}
        >
          ADD DATA SOURCE
        </Button>
      )}

      {deleteDialog && (
        <DeleteFile
          open={deleteDialog}
          handleCancel={handleDeleteFileCancel}
          handleConfirm={confirmDelete}
          subject={actionType}
          status={!selectedPlatform.status}
          fileName={"this Data Source"}
          isLoading={isDeleteDataSourcesPending || isUploadDataSourcesPending}
        />
      )}
      {error && (
        <SnackbarComponent
          error={error}
          errorMessage={errorMessage}
          hideError={hideError}
        />
      )}
      {success && (
        <SnackbarSuccessComponent
          success={success}
          successMessage={successMessage}
          hideSuccess={hideSuccess}
        />
      )}
    </Box>
  );
};

export default PlatformsComponent;
